import { Checkbox, Group, NumberInput, Select } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { MemoryDatePrecision } from 'src/Services/API/Memories.api';
import { decadeLabel, MemorySeason } from 'src/Utils/memoryDate';

import { Props } from './MemoryDateInput.types';
import { MemoryDatePrecisionChoice, MemoryDateValue } from './memoryDateValue';

const CURRENT_YEAR = new Date().getFullYear();
const FIRST_DECADE = 1900;

/**
 * The date of a memory, for people who don't always remember the exact day: a precision
 * first (day, month, season, year, decade or no date), then only the fields it needs.
 */
const MemoryDateInput = ({ value, onChange }: Props) => {
  const { t } = useTranslation();
  const set = (patch: Partial<MemoryDateValue>) => onChange({ ...value, ...patch });

  const precisions: { value: MemoryDatePrecisionChoice; label: string }[] = [
    { value: MemoryDatePrecision.Day, label: t('memories.date.day', 'Le jour exact') },
    { value: MemoryDatePrecision.Month, label: t('memories.date.month', 'Le mois') },
    { value: MemoryDatePrecision.Season, label: t('memories.date.season', 'La saison') },
    { value: MemoryDatePrecision.Year, label: t('memories.date.year', "L'année") },
    { value: MemoryDatePrecision.Decade, label: t('memories.date.decade', 'La décennie') },
    { value: 'NONE', label: t('memories.date.none', 'Je ne sais pas') },
  ];
  const months = Array.from({ length: 12 }, (_, index) => ({
    value: String(index),
    label: dayjs().month(index).format('MMMM'),
  }));
  const seasons: { value: MemorySeason; label: string }[] = [
    { value: 'SPRING', label: t('memories.seasonNames.SPRING', 'Printemps') },
    { value: 'SUMMER', label: t('memories.seasonNames.SUMMER', 'Été') },
    { value: 'AUTUMN', label: t('memories.seasonNames.AUTUMN', 'Automne') },
    { value: 'WINTER', label: t('memories.seasonNames.WINTER', 'Hiver') },
  ];
  const decades = Array.from(
    { length: Math.floor(CURRENT_YEAR / 10) - FIRST_DECADE / 10 + 1 },
    (_, index) => FIRST_DECADE + index * 10
  )
    .reverse()
    .map(decade => ({
      value: String(decade),
      label: decadeLabel(t, decade),
    }));

  const yearInput = (
    <NumberInput
      label={t('memories.date.yearLabel', 'Année')}
      placeholder={t('memories.date.yearPlaceholder', 'Ex. 1987')}
      min={1800}
      max={CURRENT_YEAR}
      allowDecimal={false}
      hideControls
      value={value.year ?? ''}
      onChange={year => set({ year: typeof year === 'number' ? year : null })}
    />
  );

  return (
    <>
      <Select
        label={t('memories.form.date', 'Date')}
        data={precisions}
        allowDeselect={false}
        value={value.precision}
        onChange={precision =>
          set({ precision: (precision ?? 'NONE') as MemoryDatePrecisionChoice })
        }
      />

      {value.precision === MemoryDatePrecision.Day && (
        <DatePickerInput
          label={t('memories.date.dayLabel', 'Jour')}
          placeholder={t('memories.date.pickDay', 'Choisir le jour')}
          valueFormat="DD/MM/YYYY"
          maxDate={new Date()}
          clearable
          value={value.day}
          onChange={day => set({ day })}
        />
      )}
      {value.precision === MemoryDatePrecision.Month && (
        <Group grow align="flex-start">
          <Select
            label={t('memories.date.monthLabel', 'Mois')}
            data={months}
            value={value.month !== null ? String(value.month) : null}
            onChange={month => set({ month: month !== null ? Number(month) : null })}
          />
          {yearInput}
        </Group>
      )}
      {value.precision === MemoryDatePrecision.Season && (
        <Group grow align="flex-start">
          <Select
            label={t('memories.date.seasonLabel', 'Saison')}
            data={seasons}
            value={value.season}
            onChange={season => set({ season: season as MemorySeason | null })}
          />
          {yearInput}
        </Group>
      )}
      {value.precision === MemoryDatePrecision.Year && yearInput}
      {value.precision === MemoryDatePrecision.Decade && (
        <Select
          label={t('memories.date.decadeLabel', 'Décennie')}
          placeholder={t('memories.date.pickDecade', 'Choisir la décennie')}
          data={decades}
          value={value.decade !== null ? String(value.decade) : null}
          onChange={decade => set({ decade: decade !== null ? Number(decade) : null })}
        />
      )}

      {value.precision !== 'NONE' && (
        <Checkbox
          label={t('memories.date.approximateLabel', 'Date approximative')}
          checked={value.approximate}
          onChange={event => set({ approximate: event.currentTarget.checked })}
        />
      )}
    </>
  );
};

export default MemoryDateInput;
